import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import { currencyFormat } from 'helpers/utils';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Transaction } from 'hooks/transactions/useTransactionsData';
import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';
import Badge from 'components/base/Badge';
import { Link } from 'react-router-dom';

// Create a type that includes all properties from ColumnDef<Transaction> plus visible property
export type TransactionColumnDef = ColumnDef<Transaction, any> & {
  visible?: boolean;
  accessorKey?: string;
  id?: string;
}

// Helper function to get the style class based on transaction type
const getTransactionTypeClass = (type: string): string => {
  // Positive transaction types (green) - credits to seller account
  const positiveTypes = ['Manual', 'rev_stock_fee', 'rev_stock_withdraw', 'rev_delay_fee', 'rev_return_fee'];
  
  // Negative transaction types (red) - debits from seller account
  const negativeTypes = ['Refund Transaction', 'stock_fee', 'stock_withdraw', 'delay_fee', 'return_fee'];
  
  if (positiveTypes.includes(type)) {
    return 'text-success';
  } else if (negativeTypes.includes(type)) {
    return 'text-danger';
  }
  
  // Default for unknown types
  return 'text-body';
};

// Helper function to determine if transaction amount should be positive or negative
const isPositiveTransaction = (type: string): boolean => {
  const positiveTypes = ['Manual', 'rev_stock_fee', 'rev_stock_withdraw', 'rev_delay_fee', 'rev_return_fee'];
  return positiveTypes.includes(type);
};

// Helper function to get readable transaction type display name
const getTransactionTypeDisplayName = (type: string, t: any): string => {
  // Use translation with fallback to the type map
  const translatedType = t(`transactions.types.${type}`, '');
  
  if (translatedType) {
    return translatedType;
  }
  
  // Fallback type map for cases where translation is not available
  const typeMap: { [key: string]: string } = {
    'Manual': 'Manual Payment',
    'Refund Transaction': 'Refund',
    'stock_fee': 'Stock Fee',
    'stock_withdraw': 'Stock Withdrawal',
    'delay_fee': 'Delay Fee',
    'return_fee': 'Return Fee',
    'rev_stock_fee': 'Rev. Stock Fee',
    'rev_stock_withdraw': 'Rev. Stock Withdrawal',
    'rev_delay_fee': 'Rev. Delay Fee',
    'rev_return_fee': 'Rev. Return Fee'
  };
  
  return typeMap[type] || type;
};

// TypeCell component to handle translation
const TypeCell: FC<{ type: string; isPositive: boolean }> = ({ type, isPositive }) => {
  const { t } = useTranslation();
  const displayName = getTransactionTypeDisplayName(type, t);
  
  return (
    <Badge 
      variant="phoenix" 
      bg={isPositive ? 'success' : 'danger'} 
      className="fw-semibold text-capitalize"
    >
      {displayName}
    </Badge>
  );
};

// Helper function to check if string contains substring (case insensitive)
const stripos = (haystack: string, needle: string): boolean => {
  return haystack.toLowerCase().indexOf(needle.toLowerCase()) !== -1;
};

// Helper function to parse message and extract order number or RMA ID from the end
const parseMessageForId = (message: string): { id: string; isOrderNumber: boolean } | null => {
  if (!message) return null;
  
  // Split by " - " and get the last part which should be the ID
  const parts = message.split(' - ');
  if (parts.length < 2) return null;
  
  const lastPart = parts[parts.length - 1].trim();
  
  // Check if it's a valid number
  if (!/^\d+$/.test(lastPart)) return null;
  
  // Determine if it's an order number (longer, typically 10 digits) or RMA ID (shorter, typically 4 digits)
  // Order numbers are usually longer than 6 digits, RMA IDs are usually shorter
  const isOrderNumber = lastPart.length >= 6;
  
  return {
    id: lastPart,
    isOrderNumber
  };
};

// MessageCell component to handle clickable order increment IDs and RMA IDs
const MessageCell: FC<{ message: string; type: string }> = ({ message, type }) => {
  const isRefundType = type && stripos(type, 'refund');
  
  // Define transaction types that should have clickable IDs
  const clickableTypes = [
    'stock_fee',
    'stock_withdraw', 
    'delay_fee',
    'return_fee',
    'rev_delay_fee',
    'rev_return_fee'
  ];
  
  const isClickableType = clickableTypes.includes(type);
  
  // For refund transactions, parse message for order increment IDs and make them clickable
  if (isRefundType && message) {
    // Split message by comma in case there are multiple order IDs
    const orderIds = message.split(',').map(id => id.trim()).filter(id => id.length > 0);
    
    if (orderIds.length > 0) {
      return (
        <span className="text-capitalize fw-semibold">
          {orderIds.map((orderId, index) => (
            <span key={orderId}>
              <Link 
                to="/orders" 
                state={{ 
                  page: 1, 
                  pageSize: 10, 
                  filter: { order_number: orderId } 
                }}
                className="text-primary fw-semibold text-decoration-none"
                title={`Search for order ${orderId}`}
              >
                {orderId}
              </Link>
              {index < orderIds.length - 1 && ', '}
            </span>
          ))}
        </span>
      );
    }
  }
  
  // For clickable types (stock_fee, stock_withdraw, etc.), parse message for order number or RMA ID
  if (isClickableType && message) {
    const parsedId = parseMessageForId(message);
    
    if (parsedId) {
      // Split the message to show everything before the ID and make the ID clickable
      const parts = message.split(' - ');
      const beforeId = parts.slice(0, -1).join(' - ');
      const idPart = parts[parts.length - 1];
      
      return (
        <span className="text-capitalize fw-semibold">
          {beforeId && beforeId + ' - '}
          <Link 
            to={parsedId.isOrderNumber ? "/orders" : `/refunds/${parsedId.id}`}
            state={parsedId.isOrderNumber ? { 
              page: 1, 
              pageSize: 10, 
              filter: { order_number: parsedId.id } 
            } : undefined}
            className="text-primary fw-semibold text-decoration-none"
            title={parsedId.isOrderNumber ? `Search for order ${parsedId.id}` : `View RMA ${parsedId.id}`}
          >
            {idPart}
          </Link>
        </span>
      );
    }
  }
  
  // For non-refund transactions or empty messages, display as regular text
  return (
    <span className="text-capitalize fw-semibold">
      {message}
    </span>
  );
};

export const transactionsTableColumns: TransactionColumnDef[] = [
  {
    accessorKey: 'transaction_id',
    header: 'Transaction ID',
    visible: true,
    cell: ({ row: { original } }) => {
      const typeClass = getTransactionTypeClass(original.type);
      const isPositive = isPositiveTransaction(original.type);
      
      // Extract first two characters for the badge
      const firstTwoChars = original.transaction_id.substring(0, 2);
      const remainingChars = original.transaction_id.substring(2);
      
      // Only make transaction ID clickable when type is "Manual"
      const content = (
        <>
          <Badge 
            variant="phoenix" 
            bg={isPositive ? 'success' : 'danger'} 
            className="me-1"
          >
            {firstTwoChars}
          </Badge>
          {remainingChars}
        </>
      );
      
      // Conditionally render Link or span based on transaction type
      return original.type === 'Manual' ? (
        <Link 
          to={`/transactions/${original.entity_transaction_id}`}
          className={`fw-semibold text-decoration-none`}
        >
          {content}
        </Link>
      ) : (
        <span className="fw-semibold">
          {content}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '12%' }, className: 'pe-3 ps-0' },
      cellProps: { className: 'ps-0' }
    }
  },
  {
    accessorKey: 'type',
    header: 'Type',
    visible: true,
    cell: ({ row: { original } }) => {
      const typeClass = getTransactionTypeClass(original.type);
      const isPositive = isPositiveTransaction(original.type);
      
      return (
        <TypeCell type={original.type} isPositive={isPositive} />
      );
    },
    meta: {
      headerProps: { style: { width: '12%' } },
      cellProps: { className: 'text-body' }
    }
  },
  {
    accessorKey: 'bank_transaction_id',
    header: 'Payment Details',
    visible: true,
    cell: ({ row: { original } }) => {
      const typeClass = getTransactionTypeClass(original.type);
      return (
        <span className={`fw-semibold text-capitalize `}>
          {original.bank_transaction_id}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '12%' } },
      cellProps: { className: 'text-body' }
    }
  },
  {
    accessorKey: 'message',
    header: 'Message',
    visible: true,
    cell: ({ row: { original } }) => {
      const typeClass = getTransactionTypeClass(original.type);
      return (
        <MessageCell message={original.message} type={original.type} />
      );
    },
    meta: {
      headerProps: { style: { width: '14%' } },
      cellProps: { className: 'text-body' }
    }
  },
  {
    accessorKey: 'formatted_date',
    header: 'Transaction Date',
    visible: true,
    cell: ({ row: { original } }) => {
      const typeClass = getTransactionTypeClass(original.type);
      return (
        <span className={`fs-9 text-capitalize fw-semibold `}>
          {original.formatted_date}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '12%' } },
      cellProps: { className: 'text-body-tertiary' }
    }
  },
  {
    accessorKey: 'transaction_amount',
    header: 'Transaction Amount',
    visible: true,
    cell: ({ row: { original } }) => {
      const isPositive = isPositiveTransaction(original.type);
      const bgClass = getTransactionTypeClass(original.type);
      const prefix = isPositive ? '+' : '–';
      return (
        <span className={`currency-value ${bgClass} fw-bold `}>
          {prefix}{Math.abs(original.transaction_amount).toFixed(2)} DT
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '12%' } },
      cellProps: { className: 'text-end fw-semibold' }
    }
  },
  {
    accessorKey: 'total_amount',
    header: 'Total Amount',
    visible: true,
    cell: ({ row: { original } }) => {
      const typeClass = getTransactionTypeClass(original.type);
      return (
        <span className={`currency-value   fw-semibold `}>
          {currencyFormat(original.total_amount)}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '10%' }},
      cellProps: { className: 'text-end fw-semibold' }
    }
  },
  {
    accessorKey: 'comission_amount',
    header: 'Commission Amount',
    visible: true,
    cell: ({ row: { original } }) => {
      const typeClass = getTransactionTypeClass(original.type);
      return (
        <span className={`currency-value  fw-semibold `}>
          {currencyFormat(original.comission_amount)}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '10%' }, className: '' },
      cellProps: { className: ' fw-semibold' }
    }
  }
];

interface TransactionsTableProps {
  visibleColumns?: Record<string, boolean>;
  onToggleColumn?: (columnId: string, visible: boolean) => void;
  totalCount?: number;
}

const TransactionsTable: FC<TransactionsTableProps> = ({ totalCount }) => {
  const { t } = useTranslation();
  const table = useAdvanceTableContext();
  
  // Create columns with translated headers and directly update them
  useMemo(() => {
    // Apply translations to the table headers by directly modifying the column definitions
    table.getAllColumns().forEach(column => {
      const accessorKey = column.id;
      if (accessorKey) {
        const translatedHeader = t(`transactions.columns.${accessorKey}`, column.columnDef.header as string);
        if (translatedHeader) {
          column.columnDef.header = translatedHeader;
        }
      }
    });
  }, [t, table]);
  return (
    <div>
      <AdvanceTable 
        tableProps={{ 
          className: 'phoenix-table fs-9',
          striped: true,
          hover: true
        }}
      />
      <AdvanceTableFooter 
        pagination 
        totalCount={totalCount} 
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </div>
  );
};

export default TransactionsTable;
